import React, { useState } from "react";
import { Brain, Server, Layout, Database, Cloud, Workflow, Users, Zap, Sparkles, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Eyebrow, PageHeader, Section, SectionHeading, CtaBand } from "../components/site";
import { Reveal, CountUp } from "../components/motion";
import { useI18n } from "../i18n";

const skillIcons = [Brain, Server, Layout, Database, Cloud, Workflow];
const whyIcons = [Zap, Sparkles, ShieldCheck];

// Front shows the area; hover (desktop) or tap (touch) flips to the tools.
const SkillCard = ({ title, tools, Icon, delay }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <Reveal delay={delay} className="[perspective:1200px]">
      <button
        type="button"
        onClick={() => setFlipped((f) => !f)}
        aria-pressed={flipped}
        className="group relative block h-44 w-full text-start"
      >
        <span
          className={`relative block h-full w-full rounded-3xl transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] ${
            flipped ? "[transform:rotateY(180deg)]" : ""
          }`}
        >
          <span className="absolute inset-0 flex flex-col justify-between rounded-3xl border border-line bg-surface p-6 [backface-visibility:hidden]">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-soft text-brand">
              <Icon size={20} />
            </span>
            <span className="text-xl font-semibold text-ink">{title}</span>
          </span>
          <span className="absolute inset-0 flex items-center rounded-3xl bg-deep p-6 font-mono text-sm leading-relaxed text-[#cfe3df] [backface-visibility:hidden] [transform:rotateY(180deg)]">
            {tools}
          </span>
        </span>
      </button>
    </Reveal>
  );
};

const About = () => {
  const { t } = useI18n();
  const stats = t("home.stats");
  const values = t("about.values");
  const skills = t("about.skills");
  const team = t("about.team");
  const why = t("about.why");

  return (
    <>
      <Header />

      <PageHeader eyebrow={t("about.eyebrow")} title={t("about.title")} lede={t("about.lede")} />

      {/* Story + numbers */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-7">
            <Eyebrow>{t("about.storyLabel")}</Eyebrow>
            <p className="mt-6 font-display text-3xl leading-[1.3] text-ink md:text-4xl">{t("about.story")}</p>
          </Reveal>
          <div className="grid grid-cols-3 gap-3 lg:col-span-5">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 100} className="rounded-3xl border border-line bg-surface p-5 text-center">
                <div className="font-display text-4xl text-brand md:text-5xl">
                  <CountUp value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-xs leading-snug text-ink-2">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* Values */}
      <Section className="border-y border-line bg-surface">
        <SectionHeading label={t("about.valuesLabel")} title={values.map((v) => v.title).join(" · ")} />
        <div className="grid gap-4 md:grid-cols-3">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 120} className="card-lift rounded-3xl border border-line bg-paper p-7">
              <span className="font-mono text-xs text-brand">0{i + 1}</span>
              <h3 className="mt-6 text-xl font-semibold text-ink">{v.title}</h3>
              <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-2">{v.desc}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Expertise: tools hidden behind flip cards */}
      <Section>
        <SectionHeading label={t("about.skillsLabel")} title={t("about.skillsTitle")} intro={t("about.skillsHint")} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {skills.map((s, i) => (
            <SkillCard key={s.title} title={s.title} tools={s.tools} Icon={skillIcons[i]} delay={i * 80} />
          ))}
        </div>
      </Section>

      {/* Team + why us */}
      <Section className="pt-0 md:pt-0">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-[2rem] border border-line bg-surface p-7 md:p-10">
            <Eyebrow>{t("about.teamLabel")}</Eyebrow>
            <ul className="mt-6 space-y-3">
              {team.map((role) => (
                <li key={role} className="flex items-center gap-3 text-lg text-ink">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft text-brand">
                    <Users size={16} />
                  </span>
                  {role}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="rounded-[2rem] bg-deep p-7 text-white md:p-10">
            <Eyebrow dark>{t("about.whyLabel")}</Eyebrow>
            <ul className="mt-6 space-y-5">
              {why.map((w, i) => {
                const Icon = whyIcons[i];
                return (
                  <li key={w.title} className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-[#6fc4b9]">
                      <Icon size={18} />
                    </span>
                    <span>
                      <span className="block text-lg font-semibold">{w.title}</span>
                      <span className="block text-[0.9375rem] text-[#b8c9c6]">{w.desc}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>
      </Section>

      <CtaBand />
      <Footer />
    </>
  );
};

export default About;
